import { Injectable } from '@angular/core';
import {
  GetObjectRequest,
  PutObjectCommand,
  PutObjectCommandOutput,
  PutObjectRequest,
  S3Client,
  S3ClientConfig,
} from '@aws-sdk/client-s3';
import { environment } from '@emma-environments/environment';
import { Observable, from } from 'rxjs';

import { patchS3CompleteMultipartUpload, uploadMultipartFile } from './s3.service.helper';

import { Sha256 } from '@aws-crypto/sha256-browser';
import { S3RequestPresigner } from '@aws-sdk/s3-request-presigner';
import { formatUrl } from '@aws-sdk/util-format-url';
import { HttpRequest } from '@smithy/protocol-http';
import { parseUrl } from '@smithy/url-parser';

export interface S3ServiceUploadOptions {
  /**
   * Return the URI as a https URL.
   */
  useHttpsScheme: boolean;
  retries: number;
  queueSize: number;
}

/**
 * 5 MB is the maximum recommended file size that should be uploaded to S3 without multi-part.
 */
const SMALL_FILE_LIMIT = 1024 * 1024 * 5;

/**
 * Service for uploading files to S3.
 * @description Direct uploads for files smaller than 5 MB, multi-part uploads for larger files.
 */
@Injectable({ providedIn: 'root' })
export class S3Service {
  private s3Client: S3Client;
  private options: S3ServiceUploadOptions;

  readonly defaultOptions: S3ServiceUploadOptions = {
    useHttpsScheme: false,
    retries: 3,
    queueSize: 4,
  };

  readonly config: S3ClientConfig = {
    apiVersion: '2006-03-01',
    region: environment.s3Region,
    endpoint: `https://s3.${environment.s3Region}.${environment.s3Host}`,
    credentials: {
      accessKeyId: environment.s3AccessKey,
      secretAccessKey: environment.s3SecretKey,
    },
  };

  constructor() {
    this.s3Client = new S3Client(this.config);
    patchS3CompleteMultipartUpload(this.s3Client);
    this.options = { ...this.defaultOptions };
  }

  upload = (params: PutObjectRequest, options?: Partial<S3ServiceUploadOptions>): Observable<string> => {
    if (options) {
      this.options = { ...this.defaultOptions, ...options };
    }
    const prom = this.uploadFile(params).then(() => {
      if (options?.useHttpsScheme) {
        return `${this.config.endpoint}/${params.Bucket}/${params.Key}`;
      }
      return `s3://${params.Key}`;
    });
    return from<Promise<string>>(prom);
  };

  /**
   * Determines if the data to be uploaded is small enough to upload in a single request.
   */
  private isLargeUpload = (body: unknown) => body instanceof Blob && body.size > SMALL_FILE_LIMIT;

  /**
   * Uploads a file to S3.
   */
  private uploadFile(params: PutObjectRequest): Promise<PutObjectCommandOutput> {
    return this.isLargeUpload(params.Body) ? this.uploadLargeFile(params) : this.uploadSmallFile(params);
  }

  /**
   * Uploads a file to S3 in a single request.
   */
  private uploadSmallFile(
    params: PutObjectRequest,
    retries = this.options.retries
  ): Promise<PutObjectCommandOutput> {
    return this.s3Client.send(new PutObjectCommand(params)).catch((err) => {
      if (err.code === 'RequestEntityTooLarge') {
        return this.uploadLargeFile(params);
      } else if (retries > 0) {
        return this.uploadSmallFile(params, retries - 1);
      } else {
        throw err;
      }
    });
  }

  /**
   * Uploads a file to S3 in multiple requests.
   */
  private uploadLargeFile(params: PutObjectRequest): Promise<PutObjectCommandOutput> {
    return uploadMultipartFile({
      client: this.s3Client,
      params,
      retries: this.options.retries,
      queueSize: this.options.queueSize,
    });
  }

  getPublicUrl(params: GetObjectRequest) {
    // from: https://aws.amazon.com/blogs/developer/generate-presigned-url-modular-aws-sdk-javascript/
    if (this.config.credentials && this.config.region) {
      const urlParams = params.ResponseContentDisposition
        ? `?response-content-disposition=${params.ResponseContentDisposition}`
        : '';
      const s3ObjectUrl = parseUrl(`${this.config.endpoint}/${params.Bucket}/${params.Key}${urlParams}`);
      const presigner = new S3RequestPresigner({
        credentials: this.config.credentials,
        region: this.config.region,
        // sha256: Hash.bind(null, 'sha256'), // In Node.js
        sha256: Sha256, // In browsers
      });
      // Create a GET request from S3 url.
      return presigner
        .presign(new HttpRequest(s3ObjectUrl), { expiresIn: 60 * 60 })
        .then((url) => formatUrl(url));
    }
    return Promise.resolve();
  }
}
